import { z } from 'zod';

import { AGGREGATES } from '#plugin-chat/lib/timeseries/aggregates.ts';
import { inputDatasetSchema, outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetProducerResult } from '#plugin-chat/lib/tool/output-types.ts';

/**
 * Result type for the FetchTimeseriesDatapointsTool execution.
 *
 * Aliases the shared {@link DatasetProducerResult} contract — no
 * tool-specific extras are carried because datapointCount, seriesCount, and
 * column lists are all derivable from `dataset.rows` / `dataset.columns`.
 */
export type FetchTimeseriesDatapointsResult = DatasetProducerResult;

/**
 * LLM-facing input schema for {@link FetchTimeseriesDatapointsTool}.
 *
 * Static schema — `inputDataset` / `columns` are plain strings; the tool
 * resolves `inputDataset` against the chat-history dataset store at
 * execute time via {@link usePipelineContext}.
 */
export const FetchTimeseriesDatapointsInputSchema = z.object({
  startTime: z.string().describe('Start of time range as ISO 8601 datetime (e.g., "2024-01-01T00:00:00Z")'),
  endTime: z.string().describe('End of time range as ISO 8601 datetime (e.g., "2024-01-07T23:59:59Z")'),
  aggregate: z
    .enum([...AGGREGATES, 'none'])
    .optional()
    .describe('How to aggregate datapoints within each time bucket. Use "average" aggregation by default.'),
  granularity: z
    .enum(['1h', '1d', '7d', '30d'])
    .optional()
    .describe('Optional aggregation bucket width. Auto-computed from the time range when omitted.'),
  outputDataset: outputDatasetSchema,
  externalIds: z
    .array(z.string())
    .optional()
    .describe('Direct timeseries external IDs (e.g., "ME010-S:WellOutputs:priorityOilRateStd").'),
  inputDataset: inputDatasetSchema
    .optional()
    .describe(
      'Optional reference to a stored dataset. When provided together with `columns`, the tool extracts timeseries externalIds from the stored rows instead of requiring inline `externalIds`.',
    ),
  columns: z
    .string()
    .array()
    .optional()
    .describe(
      "Names of timeseries-ref columns in `inputDataset` (e.g. `priorityOilRateStd`). Each row's value at these columns is an EnrichedTimeseriesRef object `{ externalId, datapoints, ... }` and the tool extracts `.externalId` automatically — pass the bare column name, NOT a dotted path. Omit to fetch all timeseries columns.",
    ),
});

/** @public Input args for FetchTimeseriesDatapointsTool */
export type FetchTimeseriesDatapointsInput = z.infer<typeof FetchTimeseriesDatapointsInputSchema>;
